/**
 * Serialises requests to the token route across every tab of this browser.
 *
 * Two requests carrying the same refresh token can otherwise be in flight at
 * once: two tabs restoring together, or two live-points tabs whose realtime
 * clients re-authenticate in the same second. Each rotates the session, the
 * backend honours the second inside its reuse grace window by rotating again,
 * and the browser keeps whichever `Set-Cookie` arrives last. If that is the
 * first response, the browser holds a refresh token the backend has already
 * rotated out, and the next refresh an hour later is refused as reuse.
 *
 * The cookie jar is per browser, so a browser-wide lock is exactly the scope
 * of the race: once the first request has resolved its cookies are applied,
 * and the next one presents the current token and is served from the cache.
 * The lock covers only the fetch, not reading the body, because cookies land
 * with the response headers.
 *
 * Where the Web Locks API is missing (old browsers, non-browser callers, the
 * test environment) the request runs unserialised, as it did before.
 */
const ACCESS_TOKEN_LOCK_NAME = 'ffh-access-token-route';

const resolveLockManager = (): LockManager | undefined =>
  typeof navigator === 'undefined' ? undefined : navigator.locks;

export const withAccessTokenLock = <T>(
  request: () => Promise<T>
): Promise<T> => {
  const lockManager = resolveLockManager();
  if (!lockManager) return request();
  return lockManager.request(ACCESS_TOKEN_LOCK_NAME, request) as Promise<T>;
};
